/**
 * TriageSettingsDialog — which status pills are on, their date parameters, and free-text guidance for the AI
 * (Loan Arrears Ageing → gear next to AI Triage). Per region.
 */
import { useEffect, useState } from "react";
import { Loader2, Save, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { getTriageCatalog, saveTriageSettings } from "@/lib/api";
import { PILL_TONES } from "@/components/collections/TriagePills";

const PARAMS = [
  { key: "broken_promise_lookback_days", label: "Broken promise — count promises that fell due in the last", unit: "days" },
  { key: "missing_payment_wait_days", label: "Missing payment — \"I've paid\" but nothing posted after", unit: "days" },
  { key: "pdn_expiry_days", label: "PDN expires (when the diary has no expiry date) after", unit: "days" },
  { key: "new_in_arrears_days", label: "New in arrears — first seen / back on the list within", unit: "days" },
  { key: "responsiveness_window_days", label: "Responsive / unresponsive — look at attempts over the last", unit: "days" },
  { key: "responsiveness_min_attempts", label: "…and need at least this many attempts to judge", unit: "attempts" },
  { key: "comms_lookback_days", label: "AI reads emails, notes and SMS from the last", unit: "days" },
  { key: "ai_min_confidence", label: "AI findings need at least this confidence to show", unit: "%" },
];

export function TriageSettingsDialog({ open, onOpenChange, settings, onSaved }) {
  const [catalog, setCatalog] = useState([]);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    getTriageCatalog().then((r) => setCatalog(r.pills || [])).catch(() => {});
    if (settings) setDraft({ auto: settings.auto, enabled: { ...settings.enabled }, params: { ...settings.params }, guidance: settings.guidance || "" });
  }, [open, settings]);

  const save = async () => {
    const blank = PARAMS.find((pr) => draft.params[pr.key] === "" || draft.params[pr.key] == null || Number.isNaN(Number(draft.params[pr.key])));
    if (blank) { toast.error(`"${blank.label}" needs a number`); return; }
    setSaving(true);
    try {
      const s = await saveTriageSettings(draft);
      onSaved?.(s);
      toast.success("Triage settings saved — they apply from the next run");
      onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = Array.isArray(d) ? d.map((x) => x.msg || JSON.stringify(x)).join("; ") : (typeof d === "string" ? d : e.message);
      toast.error("Save failed: " + msg);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="triage-settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base"><Sparkles className="w-4 h-4 text-violet-600" /> AI triage — status pills</DialogTitle>
          <DialogDescription className="text-xs">
            Pills are worked out from the file (promises, PDN diary, LoanDisk payments, contact history) plus an AI read of recent emails, notes and SMS.
            Runs after every arrears sync and every 30 minutes while Auto-triage is on; the AI only looks at accounts with new activity.
          </DialogDescription>
        </DialogHeader>
        {!draft ? <div className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> loading…</div> : (
          <div className="space-y-4 text-xs">
            <div>
              <div className="font-semibold text-slate-700 mb-1.5">Pills</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5">
                {catalog.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 py-1 cursor-pointer" data-testid={`triage-pill-toggle-${p.key}`}>
                    <Switch checked={draft.enabled[p.key] !== false} onCheckedChange={(v) => setDraft((d) => ({ ...d, enabled: { ...d.enabled, [p.key]: v } }))} className="scale-75 mt-0.5" />
                    <span className="min-w-0">
                      <span className={`inline-block text-[9px] font-bold tracking-wide px-1.5 h-4 leading-4 rounded border ${PILL_TONES[p.tone] || PILL_TONES.slate}`}>{p.label}</span>
                      {p.ai && <Sparkles className="inline w-3 h-3 ml-1 text-violet-600 align-text-bottom" title="Needs the AI read" />}
                      <span className="block text-[11px] text-muted-foreground leading-snug mt-0.5">{p.help}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-700 mb-1.5">Date parameters</div>
              <div className="space-y-1.5">
                {PARAMS.map((pr) => (
                  <div key={pr.key} className="flex items-center gap-2" data-testid={`triage-param-${pr.key}`}>
                    <span className="flex-1 text-[11px] text-slate-700">{pr.label}</span>
                    <Input type="number" min="0" value={draft.params[pr.key] ?? ""} onChange={(e) => setDraft((d) => ({ ...d, params: { ...d.params, [pr.key]: e.target.value === "" ? "" : Number(e.target.value) } }))}
                      className="h-7 w-[76px] text-xs text-right" data-testid={`triage-param-input-${pr.key}`} />
                    <span className="w-16 text-[11px] text-muted-foreground">{pr.unit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="font-semibold text-slate-700 mb-1">Guidance for the AI</div>
              <Textarea rows={3} value={draft.guidance} onChange={(e) => setDraft((d) => ({ ...d, guidance: e.target.value }))} className="text-xs"
                placeholder="e.g. A customer asking for a payment plan is not a dispute. Treat 'the funds left my account' as paid only when a date or reference is given." data-testid="triage-guidance" />
            </div>
            <div className="flex items-center justify-between pt-1">
              <span className="text-[10px] text-muted-foreground">
                {settings?.last_run?.at ? `Last run ${new Date(settings.last_run.at).toLocaleString("en-GB")} · ${settings.last_run.cases} files · ${settings.last_run.ai_calls} AI reads` : "Not run yet"}
                {settings?.updated_at ? ` · settings saved ${new Date(settings.updated_at).toLocaleDateString("en-GB")} by ${settings.updated_by || "—"}` : ""}
              </span>
              <Button size="sm" className="h-8 text-xs" onClick={save} disabled={saving} data-testid="triage-settings-save">
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />} Save
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
